import { useQuery } from '@tanstack/react-query'
import { Eye, GitBranch, Trash2 } from 'lucide-react'
import { apiClient } from '../lib/api'
import { formatDate } from '../lib/utils'
import { Badge, type Variant } from './ui/Badge'
import { Button } from './ui/Button'
import { Dialog } from './ui/Dialog'
import { Empty, Skeleton } from './ui/Empty'

type RevisionRow = {
  id: string
  code: string
  revision: number
  status: string
  parentProposalId: string | null
  supersededByProposalId: string | null
  deletedAt: string | null
  updatedAt: string
  clientName: string | null
  notes: string | null
}

type HistoryResult = {
  found: boolean
  code?: string
  total?: number
  revisions: RevisionRow[]
}

const statusVariant: Record<string, Variant> = {
  draft: 'muted',
  sent: 'info',
  accepted: 'success',
  rejected: 'danger',
  expired: 'warning',
  superseded: 'muted',
}

const statusLabel: Record<string, string> = {
  draft: 'Bozza',
  sent: 'Inviata',
  accepted: 'Accettata',
  rejected: 'Rifiutata',
  expired: 'Scaduta',
  superseded: 'Superata',
}

export function ProposalHistoryDialog({
  proposalId,
  currentRevision,
  onClose,
  onOpenRevision,
}: {
  proposalId: string
  currentRevision: number
  onClose: () => void
  onOpenRevision?: (id: string) => void
}) {
  const historyQ = useQuery({
    queryKey: ['proposal-history', proposalId],
    queryFn: () =>
      apiClient.get<HistoryResult>(`/proposals/${proposalId}/history?includeDeleted=true`),
  })

  const revisions = historyQ.data?.revisions ?? []
  // Show newest first
  const ordered = [...revisions].sort((a, b) => b.revision - a.revision)

  return (
    <Dialog
      open
      onClose={onClose}
      title={
        historyQ.data?.code ? `Storico revisioni — ${historyQ.data.code}` : 'Storico revisioni'
      }
      size="lg"
    >
      <div className="p-5">
        {historyQ.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : ordered.length === 0 ? (
          <Empty
            icon={GitBranch}
            title="Nessuna revisione trovata"
            description="Questa proposta non ha revisioni multiple registrate."
          />
        ) : (
          <>
            <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
              {ordered.length} {ordered.length === 1 ? 'revisione' : 'revisioni'} di{' '}
              {historyQ.data?.code}. La R più alta è la versione corrente, le revisioni vecchie sono
              in stato <span className="text-zinc-400">superseded</span>.
            </p>
            <ol className="space-y-2">
              {ordered.map((r) => {
                const isCurrent = r.revision === currentRevision
                return (
                  <li
                    key={r.id}
                    className={`rounded-lg border p-3 ${
                      isCurrent
                        ? 'border-cyan-500/40 bg-cyan-500/5'
                        : 'border-zinc-800 bg-zinc-950/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Revision marker */}
                      <div
                        className={`shrink-0 size-10 rounded-full grid place-items-center text-sm font-bold ${
                          isCurrent
                            ? 'bg-gradient-to-br from-cyan-500 to-violet-600 text-white shadow-[0_0_12px_-2px_rgba(34,211,238,0.6)]'
                            : 'bg-zinc-800 text-zinc-400'
                        }`}
                      >
                        R{r.revision}
                      </div>

                      {/* Body */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs text-zinc-400">
                            {r.code} R{r.revision}
                          </span>
                          <Badge variant={statusVariant[r.status] ?? 'default'}>
                            {statusLabel[r.status] ?? r.status}
                          </Badge>
                          {isCurrent && <Badge variant="brand">Corrente</Badge>}
                          {r.deletedAt && (
                            <Badge variant="danger">
                              <Trash2 className="size-2.5" />
                              Eliminata
                            </Badge>
                          )}
                          <span className="text-[11px] text-zinc-500">
                            Aggiornata {formatDate(r.updatedAt)}
                          </span>
                        </div>
                        {r.notes && (
                          <p className="text-xs text-zinc-500 mt-1 line-clamp-2" title={r.notes}>
                            {r.notes}
                          </p>
                        )}
                        {r.parentProposalId && (
                          <p className="text-[10.5px] text-zinc-600 mt-1 font-mono">
                            ← derivata da R{r.revision - 1}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="shrink-0 flex flex-col gap-1.5">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => window.open(`/api/proposals/${r.id}/preview`, '_blank')}
                        >
                          <Eye className="size-3.5" /> Anteprima
                        </Button>
                        {onOpenRevision && !isCurrent && !r.deletedAt && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              onOpenRevision(r.id)
                              onClose()
                            }}
                          >
                            Apri
                          </Button>
                        )}
                      </div>
                    </div>
                  </li>
                )
              })}
            </ol>
          </>
        )}
      </div>
    </Dialog>
  )
}
